import { findIndex } from 'lodash';
import { createSelector } from 'reselect';
import {
  CONSTANTS,
  EARLY_WITHDRAWAL_PENALTY_AGE,
  FundNoteAutoExchange,
} from 'src/settings/settings';
import { validateExchangeFromFund } from 'src/utils/form/validate/validate-exchange';
import {
  updateAutoExchangeFromFundNotes,
  updateAutoExchangeToFundNotes,
} from 'src/utils/funds/autoExchange/auto-exchange-fund-notes-utility';
import { getRecurringTransactionsForFund } from 'src/utils/funds/autoExchange/auto-exchange-utils';
import {
  calculateSellTransactionFundDetails,
  getAutoExchangeFromFundLimits,
  isProductType529,
  isProductTypeMutualFunds,
  isRetirementFund,
  isSelectedWithAmount,
  setExchangeFromFundLimitMessage,
} from 'src/utils/funds/funds-utility';
import {
  DISABLE_ON_MATCH,
  updateFundNote,
} from 'src/utils/funds/shared/shared-fund-notes-utility';
import { coalesce } from 'src/utils/utils';
import config from '../../../config/config';
import { AGREEMENT_TYPES } from '../../../settings/settings';
import { getMonthName } from '../../../utils/dates/date-utility';
import { appendBasePath } from '../../../utils/nav.utils';
import { automaticEditActivityID } from '../automaticEdit/automaticEdit.selectors';
import {
  getAutoExchanges,
  getAutomaticTransactions,
  isRecurringExchangeTuple as recurringExchangeTuple,
} from '../automaticInvestments/automaticInvestments.selectors';
import {
  getAutoExchangeFunds,
  getCartLookup,
} from '../shoppingCart/shoppingCart.selectors';
import { getUserStateWithholdingRules } from '../stateWithholding/selectors';
import { getPersonalInformationData } from '../user/PersonalInformation/personalInformation.selectors';
import {
  getExchangeFederalWithholdingDollars,
  getExchangeFromFundListWithout529,
  getExchangeToFundList,
} from './exchangeTransaction.selectors';
import { isAutoExchangeEdit } from 'src/utils/transactions-util';
import {
  getIsRMDEligible,
  getSystematicActivityRMDs,
} from '../RMD/RMDSetting.selectors';

export const getAutoExchangeFromFundList = createSelector(
  getAutomaticTransactions,
  getExchangeFromFundListWithout529,
  getUserStateWithholdingRules,
  getSystematicActivityRMDs,
  (
    automaticTransactions,
    exchangeFromFundList,
    userStateWithholdingRules,
    existingRMDs
  ) =>
    exchangeFromFundList.map((fund) => {
      const { autoMax } = automaticTransactions;
      const recurringTransactionTypes = getRecurringTransactionsForFund(
        fund,
        automaticTransactions
      );
      const hasRecurring = recurringTransactionTypes.length > 0;

      const recurringFund = {
        ...fund,
        notes: {},
        disabled: false,
        hasIRAAutoMax: !!autoMax[fund.agreementID],
        hasRecurring,
        recurringTransactionTypes,
        transactionLimit: getAutoExchangeFromFundLimits(),
      };

      // Need to reset validations based on new transactions
      setExchangeFromFundLimitMessage(recurringFund);
      validateExchangeFromFund(recurringFund);
      // Calculate withholdings
      calculateSellTransactionFundDetails(
        recurringFund,
        userStateWithholdingRules,
        true
      );
      // Need to reset fund notes and apply them for recurring
      updateAutoExchangeFromFundNotes(recurringFund, undefined, existingRMDs);
      return recurringFund;
    })
);

export const getAutoExchangeFromEditFund = createSelector(
  getAutoExchangeFromFundList,
  getAutoExchangeFunds,
  getUserStateWithholdingRules,
  automaticEditActivityID,
  (
    exchangeFromFundList,
    autoExchangeFunds,
    userStateWithholdingRules,
    automaticEditActivityID
  ) => {
    const index = findIndex(autoExchangeFunds, (fund) => {
      return automaticEditActivityID === fund.encryptedUniqueKey;
    });
    if (automaticEditActivityID && autoExchangeFunds.length > 0 && index < 0) {
      window.location.replace(`${config.url}${appendBasePath('/automatic')}`);
    }
    const editFund = index >= 0 ? autoExchangeFunds[index] : {};
    const newFund = exchangeFromFundList.filter(
      (fund) => fund.agreementID === editFund.agreementID
    )[0];
    return [
      {
        ...editFund,
        ...newFund,
        transferMonths: editFund.transferMonths
          ? editFund.transferMonths.map((month) => {
              return { label: getMonthName(month).toUpperCase(), value: month };
            })
          : [],
        transferDays: editFund.transferDays
          ? editFund.transferDays.map((day) => {
              return { label: `${day}`, value: day };
            })
          : [],
      },
    ];
  }
);
/**
 * Returns the selected recurring exchange FROM fund
 */
export const getAutoExchangeFromSummaryFund = createSelector(
  getAutoExchangeFromFundList,
  (AutoExchangeFromFundList) => {
    const selectedFund = AutoExchangeFromFundList.filter(isSelectedWithAmount);
    return selectedFund.length > 0 ? selectedFund[0] : {};
  }
);

/**
 * Returns a boolean indicating if the 'FROM' fund is RMD eligible,
 * includes check to make sure user is eligible for rmd
 */
export const getAutoExchangeFromFundEligibleForRMD = createSelector(
  getIsRMDEligible,
  getAutoExchangeFromSummaryFund,
  (isRMDEligible, AutoExchangeFromSummaryFund) => {
    return (
      isRMDEligible &&
      (AutoExchangeFromSummaryFund.agreementType === CONSTANTS.ROTH ||
        AutoExchangeFromSummaryFund.agreementType === CONSTANTS.TRADITIONAL)
    );
  }
);

export const getAutoExchangeToEditFundID = createSelector(
  getAutoExchangeFunds,
  automaticEditActivityID,
  (autoExchangeFunds, automaticEditActivityID) => {
    const transaction = autoExchangeFunds.filter((fund) => {
      return automaticEditActivityID === fund.encryptedUniqueKey;
    });
    return transaction[0]?.toFund?.agreementID;
  }
);

export const getAutoExchangeToFundList = createSelector(
  [
    getAutomaticTransactions,
    getExchangeToFundList,
    getAutoExchangeFromFundEligibleForRMD,
    getAutoExchangeFromSummaryFund,
    getAutoExchanges,
    getAutoExchangeToEditFundID,
  ],
  (
    automaticTransactions,
    exchangeToFundList,
    autoExchangeFromFundEligibleForRMD,
    autoExchangeFromSummaryFund,
    autoExchanges,
    autoExchangeToEditFundID
  ) => {
    const isRecurringExchangeTuple = recurringExchangeTuple(autoExchanges);
    return exchangeToFundList
      .filter((toFund) => {
        const isCollege = isProductType529(toFund.productType);
        const isRetFund = isRetirementFund(autoExchangeFromSummaryFund);
        const isMutualFund = isProductTypeMutualFunds(toFund.productType);
        const agreementIDsMatch =
          autoExchangeFromSummaryFund.agreementID === toFund.agreementID;

        return (
          !isCollege && ((isRetFund && isMutualFund) || !agreementIDsMatch)
        );
      })
      .map((fund) => {
        const { autoMax } = automaticTransactions;
        const recurringTransactionTypes = getRecurringTransactionsForFund(
          fund,
          automaticTransactions
        );
        const hasRecurring = recurringTransactionTypes.length > 0;
        const showRmdDisclosureNote =
          autoExchangeFromFundEligibleForRMD &&
          ![AGREEMENT_TYPES.TRADITIONAL_IRA, AGREEMENT_TYPES.ROTH_IRA].includes(
            fund.agreementType
          );

        const recurringFund = {
          ...fund,
          notes: {},
          disabled: false,
          hasIRAAutoMax: !!autoMax[fund.agreementID],
          hasRecurring,
          recurringTransactionTypes,
          showRmdDisclosureNote,
        };
        // TODO: We shouldn't be mutating in a selector. It's really hard to debug
        // This should be dispatched from somewhere else
        // Need to reset fund notes and apply recurring specific ones
        updateAutoExchangeToFundNotes(
          recurringFund,
          autoExchangeFromSummaryFund
        );
        // one-off fund note that needs context from redux and out current agreementIDs
        updateFundNote(
          recurringFund,
          isRecurringExchangeTuple([
            autoExchangeFromSummaryFund.agreementID,
            recurringFund.agreementID,
          ]) &&
            (!isAutoExchangeEdit() ||
              (isAutoExchangeEdit() &&
                recurringFund.agreementID !== autoExchangeToEditFundID)),
          FundNoteAutoExchange.AUTO_EXCHANGE_TO,
          DISABLE_ON_MATCH
        );

        return recurringFund;
      });
  }
);

/**
 * Returns the selected recurring exchange TO fund
 */
export const getAutoExchangeToSummaryFund = createSelector(
  getAutoExchangeToFundList,
  (autoExchangeToFundList) => {
    const selectedFund = autoExchangeToFundList.filter(
      ({ selected }) => selected
    );
    return selectedFund.length > 0 ? selectedFund[0] : {};
  }
);

export const getAutoExchangeStartDate = createSelector(
  getCartLookup,
  getAutoExchangeFromSummaryFund,
  (cartLookup, AutoExchangeFromSummaryFund) => {
    const { agreementID } = AutoExchangeFromSummaryFund;
    return cartLookup?.[agreementID]?.startDate || '';
  }
);

export const getAutoExchangeAmountLessWithholding = createSelector(
  getAutoExchangeFromSummaryFund,
  getExchangeFederalWithholdingDollars,
  (autoExchangeFromSummaryFund, exchangeFederalWithholdingDollars) => {
    const { grossAmount, stateWithholdingDollars } =
      autoExchangeFromSummaryFund;
    return (
      grossAmount -
      coalesce(exchangeFederalWithholdingDollars, 0) -
      coalesce(stateWithholdingDollars, 0)
    );
  }
);

export const getAutoExchangeHasEarlyWithdrawalPenalty = createSelector(
  getPersonalInformationData,
  ({ age }) => age < EARLY_WITHDRAWAL_PENALTY_AGE
);
